import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Avatar,
  Button,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  FamilyRestroom as ParentIcon,
  EventNote as AttendanceIcon,
  Message as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { studentService, examService, feedbackService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import { DataTable } from '../../components/Common';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

const StudentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [examResults, setExamResults] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState(null);
  const [feedbackData, setFeedbackData] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  useEffect(() => {
    const fetchStudentBasicInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use students/details.php endpoint to fetch student details
        const response = await fetch(`${API_ENDPOINTS.STUDENT_DETAILS}?student_id=${id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.success && data.data) {
          setStudentData({
            student: data.data.student || data.data,
            marks: data.data.marks || null,
            attendance: data.data.attendance || null,
            attendance_stats: data.data.attendance_stats || null,
            subjects: data.data.subjects || null,
          });
          // Set attendance stats separately for easy access
          setAttendanceStats(data.data.attendance_stats || null);
        } else if (data.data) {
          setStudentData({
            student: data.data.student || data.data,
            marks: data.data.marks || null,
            attendance: data.data.attendance || null,
            attendance_stats: data.data.attendance_stats || null,
            subjects: data.data.subjects || null,
          });
          // Set attendance stats separately for easy access
          setAttendanceStats(data.data.attendance_stats || null);
        } else {
          setError('Student not found');
        }
      } catch (err) {
        console.error('Error fetching student details:', err);
        setError(err.message || 'Failed to fetch student details');
      } finally {
        setLoading(false);
      }
    };

    const fetchExams = async () => {
      try {
        setLoadingExams(true);
        const response = await examService.getAll();
        if (response.success && Array.isArray(response.data)) {
          setExams(response.data);
        } else if (Array.isArray(response)) {
          setExams(response);
        }
      } catch (err) {
        console.error('Error fetching exams:', err);
      } finally {
        setLoadingExams(false);
      }
    };

    if (id) {
      fetchStudentBasicInfo();
      fetchExams();
    }
  }, [id]);

  const handleViewResults = async () => {
    if (!selectedExam) {
      return;
    }

    try {
      setResultsLoading(true);
      const response = await studentService.getDetails(id);
      if (response.success) {
        // Filter marks for the selected exam
        const allMarks = response.data.marks || [];
        const filteredMarks = allMarks.filter(
          (examData) => examData.exam_id === selectedExam || examData.id === selectedExam
        );
        
        setExamResults(filteredMarks);
        setResultsLoaded(true);
        setActiveTab(0);
      } else {
        setError(response.error || 'Failed to fetch results');
      }
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(err.message || 'Failed to fetch results');
    } finally {
      setResultsLoading(false);
    }
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!id) return;
      
      try {
        setLoadingFeedback(true);
        const response = await feedbackService.getAll({ student_id: id });
        const feedback = response.data || response || [];
        if (Array.isArray(feedback)) {
          // Sort by created_at descending
          feedback.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setFeedbackData(feedback);
        }
      } catch (err) {
        console.error('Error fetching feedback:', err);
      } finally {
        setLoadingFeedback(false);
      }
    };

    if (activeTab === 2 && id) {
      fetchFeedback();
    }
  }, [activeTab, id]);

  const handleBack = () => {
    window.history.back();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 2.5, md: 3 },
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton onClick={handleBack} color="primary">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Back to Students</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!studentData) {
    return null;
  }

  const { student, marks, attendance, attendance_stats, subjects } = studentData;
  const parentData = {
    name: student.parent_name || '—',
    email: student.parent_email || '—',
    phone: student.parent_phone || '—',
    address: student.parent_address || '—',
    father_name: student.father_name || '—',
    mother_name: student.mother_name || '—',
    alternate_phone_number: student.alternate_phone_number || '—',
  };

  // Prepare results data for table from selected exam
  const resultsData = [];
  if (examResults && Array.isArray(examResults) && examResults.length > 0) {
    examResults.forEach((examData) => {
      if (examData.subjects && Array.isArray(examData.subjects)) {
        examData.subjects.forEach((subject) => {
          const marksObtained = subject.marks !== null && subject.marks !== undefined ? subject.marks : null;
          const maxMarks = subject.max_marks !== null && subject.max_marks !== undefined ? subject.max_marks : null;
          
          // Calculate percentage if marks are available
          let calculatedPercentage = null;
          if (marksObtained !== null && maxMarks !== null && maxMarks > 0) {
            calculatedPercentage = ((marksObtained / maxMarks) * 100).toFixed(2);
          }
          
          resultsData.push({
            id: `${examData.exam_id || examData.id || ''}-${subject.subject_id || ''}`,
            exam_name: examData.exam_name || '—',
            exam_date: examData.exam_start_date || '—',
            subject_name: subject.subject_name || '—',
            marks_obtained: marksObtained !== null ? marksObtained : '—',
            max_marks: maxMarks !== null ? maxMarks : '—',
            percentage: calculatedPercentage !== null ? parseFloat(calculatedPercentage) : (subject.percentage !== null && subject.percentage !== undefined ? subject.percentage : null),
            grade: subject.grade || '—',
          });
        });
      }
    });
  }

  const resultsColumns = [
    {
      header: 'Subject',
      field: 'subject_name',
      render: (value) => (
        <Typography sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
      ),
    },
    {
      header: 'Marks',
      field: 'marks_obtained',
      render: (value, row) => {
        if (value === '—' || row.max_marks === '—') return '—';
        return `${value} / ${row.max_marks}`;
      },
    },
    {
      header: 'Percentage',
      field: 'percentage',
      render: (value, row) => {
        // Calculate percentage if marks are available
        if (row.marks_obtained !== '—' && row.max_marks !== '—' && 
            typeof row.marks_obtained === 'number' && typeof row.max_marks === 'number' && 
            row.max_marks > 0) {
          const calculated = ((row.marks_obtained / row.max_marks) * 100).toFixed(2);
          return `${calculated}%`;
        }
        // If percentage is already calculated in the data (fallback)
        if (value !== null && value !== undefined && value !== '—' && typeof value === 'number') {
          return `${value.toFixed(2)}%`;
        }
        // If value is a string number, try to parse it
        if (value !== null && value !== undefined && value !== '—' && typeof value === 'string' && !isNaN(value)) {
          return `${parseFloat(value).toFixed(2)}%`;
        }
        return '—';
      },
    },
    {
      header: 'Result',
      field: 'grade',
      render: (value, row) => {
        // Calculate Pass/Fail based on percentage
        let result = 'F';
        if (row.marks_obtained !== '—' && row.max_marks !== '—' && row.max_marks > 0) {
          const percentage = (row.marks_obtained / row.max_marks) * 100;
          result = percentage >= 40 ? 'P' : 'F';
        } else if (value !== '—' && value) {
          // Use grade if available
          const gradeVal = typeof value === 'string' ? value.toUpperCase() : value;
          if (gradeVal === 'P' || gradeVal === 'PASS' || (typeof gradeVal === 'string' && !gradeVal.includes('F'))) {
            result = 'P';
          }
        }
        return (
          <Chip
            label={result}
            size="small"
            color={result === 'P' ? 'success' : 'error'}
            sx={{ fontWeight: 700, bgcolor: result === 'P' ? 'darkgreen' : 'darkred', color: 'white' }}
          />
        );
      },
    },
  ];

  const handleGeneratePDF = async () => {
    try {
      // Generate PDF for this student
      const response = await fetch(`${API_ENDPOINTS.STUDENTS_GENERATE_PDF}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_id: id,
          class_id: student.class_id,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `Student_${student.full_name || 'Details'}_${new Date().toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(error.message || 'Failed to generate PDF. Please try again.');
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Header 
        onMenuClick={() => setSidebarOpen(true)} 
        showBackButton={true}
        onBack={handleBack}
        address={studentData?.student?.parent_address || null}
      />
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${0}px` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'background.default',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {/* Basic Details Section - Compact Header */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.03) 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap', flex: 1 }}>
                {/* Avatar */}
                <Avatar
                  sx={{
                    width: { xs: 56, sm: 64 },
                    height: { xs: 56, sm: 64 },
                    bgcolor: 'primary.main',
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(17, 82, 99, 0.3)',
                  }}
                >
                  {student.full_name?.charAt(0)?.toUpperCase() || 'S'}
                </Avatar>

                {/* Student Info */}
                <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {student.full_name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Roll No:</strong> {student.roll_number || '—'}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Class:</strong> {student.class_name || '—'}
                      {student.class_section ? ` - ${student.class_section}` : ''}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      <strong>DOB:</strong> {formatDate(student.date_of_birth)}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> {parentData.phone}
                    </Typography>
                    <Chip
                      label={student.is_active ? 'Active' : 'Inactive'}
                      color={student.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600, height: 22 }}
                    />
                  </Box>
                </Box>
              </Box>
              
              {/* Generate PDF Button */}
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleGeneratePDF}
                size="medium"
                sx={{ 
                  ml: 'auto',
                  flexShrink: 0
                }}
              >
                Generate PDF
              </Button>
            </Box>
          </Paper>

          {/* Tabs Section */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 64,
                  fontSize: '0.9375rem',
                },
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                icon={<AssignmentIcon />}
                iconPosition="start"
                label="Results"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
              <Tab
                icon={<AttendanceIcon />}
                iconPosition="start"
                label="Attendance"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
              <Tab
                icon={<FeedbackIcon />}
                iconPosition="start"
                label="Feedback Received"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
              <Tab
                icon={<ParentIcon />}
                iconPosition="start"
                label="Parent Details"
                sx={{ minWidth: { xs: 'auto', sm: 200 } }}
              />
            </Tabs>

            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Results Tab */}
              {activeTab === 0 && (
                <Box>
                  <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel id="exam-select-label">Select Exam</InputLabel>
                      <Select
                        labelId="exam-select-label"
                        id="exam-select"
                        value={selectedExam}
                        label="Select Exam"
                        onChange={(e) => {
                          setSelectedExam(e.target.value);
                          setResultsLoaded(false);
                          setExamResults([]);
                        }}
                        disabled={loadingExams}
                      >
                        {exams.map((exam) => (
                          <MenuItem key={exam.id} value={exam.id}>
                            {exam.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleViewResults}
                      disabled={!selectedExam || resultsLoading}
                      startIcon={resultsLoading ? <CircularProgress size={20} color="inherit" /> : <AssignmentIcon />}
                      sx={{ px: 3, py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      {resultsLoading ? 'Loading...' : 'View Result'}
                    </Button>
                  </Box>

                  {resultsLoaded && resultsData.length > 0 ? (
                    <DataTable
                      data={resultsData}
                      columns={resultsColumns}
                      emptyMessage="No results found"
                      showActions={false}
                    />
                  ) : resultsLoaded && resultsData.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No exam results available for this exam.
                    </Alert>
                  ) : !resultsLoaded && selectedExam ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                      <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        Select an exam and click "View Result"
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                        Choose an exam from the dropdown above and click the "View Result" button to see the results.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                      <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                        Select an Exam
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                        Please select an exam from the dropdown above to view results.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Attendance Tab */}
              {activeTab === 1 && (
                <Box>
                  {/* Info Message */}
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        mb: 1,
                      }}
                    >
                      The statistics below show the last 14 days of attendance. Keep track of your child's attendance status to ensure they maintain good attendance throughout the academic year.
                    </Typography>
                  </Box>

                  {/* Attendance Stats */}
                  {attendanceStats && (
                    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 1, flexWrap: 'wrap' }}>
                          {/* Present */}
                          <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                            <Typography variant="h6" color="success.main" fontWeight={700}>
                              {attendanceStats.total_present || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Present
                            </Typography>
                          </Box>
                          
                          {/* Absent */}
                          <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                            <Typography variant="h6" color="error.main" fontWeight={700}>
                              {attendanceStats.total_absent || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Absent
                            </Typography>
                          </Box>
                          
                          {/* Total Days */}
                          <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                            <Typography variant="h6" color="primary.main" fontWeight={700}>
                              {attendanceStats.total_records || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              Days
                            </Typography>
                          </Box>
                          
                          {/* Percentage */}
                          {attendanceStats.total_percentage !== undefined && (
                            <Box sx={{ textAlign: 'center', minWidth: { xs: '20%', sm: 'auto' } }}>
                              <Typography variant="h6" fontWeight={700} color="primary.main">
                                {attendanceStats.total_percentage}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                Total
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Attendance List */}
                  {!studentData?.attendance || studentData.attendance.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No attendance records found for this student.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {studentData.attendance.map((record, idx) => {
                        const date = new Date(record.date);
                        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
                        const formattedDate = formatDate(record.date);
                        const status = record.status || 'Pending';
                        const isPending = status === 'Pending';
                        
                        return (
                          <Card
                            key={record.id || idx}
                            sx={{
                              borderRadius: 2,
                              boxShadow: 1,
                              py: 0.5,
                            }}
                          >
                            <CardContent sx={{ py: '8px !important', px: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight={600}
                                    sx={{ 
                                      minWidth: 45,
                                      fontSize: '0.75rem',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    {weekday}
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{ 
                                      flex: 1,
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {formattedDate}
                                  </Typography>
                                </Box>
                                <Chip
                                  icon={
                                    status === 'Present' ? <CheckCircleIcon /> : 
                                    status === 'Absent' ? <CancelIcon /> : 
                                    <AccessTimeIcon />
                                  }
                                  label={status}
                                  size="small"
                                  color={
                                    status === 'Present' ? 'success' : 
                                    status === 'Absent' ? 'error' : 
                                    'warning'
                                  }
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    '& .MuiChip-icon': {
                                      fontSize: '16px',
                                    },
                                  }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {/* Feedback Received Tab */}
              {activeTab === 2 && (
                <Box>
                  {loadingFeedback ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : feedbackData.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {feedbackData.map((feedback) => (
                        <Card key={feedback.id} elevation={1} sx={{ borderRadius: 2 }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 2 }}>
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                                  From: {feedback.teacher_name || 'Admin'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {feedback.teacher_email || '—'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Date: {formatDateTime(feedback.created_at)}
                                </Typography>
                              </Box>
                              <Chip
                                label={feedback.is_read ? 'Read' : 'Unread'}
                                color={feedback.is_read ? 'success' : 'warning'}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {feedback.message}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No feedback received for this student.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Parent Details Tab */}
              {activeTab === 3 && (
                <Box>
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <CardContent>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Parent/Guardian Name
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                              {parentData.name}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Email
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {parentData.email}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Alternate Phone Number
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {parentData.alternate_phone_number !== '—' && parentData.alternate_phone_number ? parentData.alternate_phone_number : '—'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Address
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {parentData.address}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Admission Date
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {formatDate(student.admission_date)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Father Name
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {parentData.father_name}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Mother Name
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {parentData.mother_name}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default StudentView;

